import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import GameWrapper from '@/pages/jeux/components/GameWrapper';
import GameOverModal from '@/pages/jeux/components/GameOverModal';
import { useGame } from '@/contexts/GameContext';
import { MEDICAL_WORDS } from '@/mocks/mots-medicaux';
import { HEALTH_TIPS } from '@/mocks/jeux-data';
import type { CrosswordWord } from '@/types/games';

type GridSize = 'petit' | 'moyen' | 'grand';
interface PlacedWord extends CrosswordWord {
  number: number;
  cells: [number, number][];
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function generateCrossword(words: typeof MEDICAL_WORDS, size: GridSize): { grid: string[][]; clues: PlacedWord[] } {
  const gridSizeMap = { petit: 12, moyen: 15, grand: 18 };
  const wordCountMap = { petit: 6, moyen: 10, grand: 15 };
  const gs = gridSizeMap[size];
  const targetCount = wordCountMap[size];

  const selected = shuffle(words);
  const grid: string[][] = Array(gs).fill(null).map(() => Array(gs).fill(''));
  const clues: PlacedWord[] = [];
  let nextNumber = 1;

  // Try to place each word
  for (const w of selected) {
    if (clues.length >= targetCount) break;

    const word = w.word.toUpperCase().replace(/[^A-Z]/g, '');
    if (word.length < 3 || word.length > gs) continue;

    let placed = false;
    const directions: ('across' | 'down')[] = shuffle(['across', 'down']);

    // Strategy 1: try to intersect with existing words
    if (clues.length > 0) {
      for (const dir of directions) {
        const otherDir = dir === 'across' ? 'down' : 'across';
        const placedOthers = clues.filter(c => c.direction === otherDir);

        for (const other of shuffle(placedOthers)) {
          for (let wi = 0; wi < word.length && !placed; wi++) {
            for (let oi = 0; oi < other.cells.length && !placed; oi++) {
              if (word[wi] !== other.word.toUpperCase().replace(/[^A-Z]/g, '')[oi]) continue;

              const [or, oc] = other.cells[oi];
              let startR: number, startC: number;
              if (dir === 'across') {
                startR = or;
                startC = oc - wi;
              } else {
                startR = or - wi;
                startC = oc;
              }

              if (startR < 0 || startC < 0) continue;
              if (dir === 'across' && startC + word.length > gs) continue;
              if (dir === 'down' && startR + word.length > gs) continue;

              // Validate placement
              let canPlace = true;
              for (let i = 0; i < word.length && canPlace; i++) {
                const rr = dir === 'across' ? startR : startR + i;
                const cc = dir === 'across' ? startC + i : startC;
                const existing = grid[rr][cc];
                if (existing !== '' && existing !== word[i]) canPlace = false;
              }
              if (!canPlace) continue;

              // Check adjacent cells to prevent adjacent words
              for (let i = 0; i < word.length && canPlace; i++) {
                const rr = dir === 'across' ? startR : startR + i;
                const cc = dir === 'across' ? startC + i : startC;
                if (dir === 'across') {
                  if (rr > 0 && grid[rr - 1][cc] !== '' && (i === 0 || grid[rr][cc] === '')) {
                    const isPartOfVertical = clues.some(cl =>
                      cl.direction === 'down' && cl.cells.some(([r, c]) => r === rr - 1 && c === cc)
                    );
                    if (!isPartOfVertical && i === 0) canPlace = false;
                  }
                  if (rr < gs - 1 && grid[rr + 1][cc] !== '' && (i === 0 || grid[rr][cc] === '')) {
                    const isPartOfVertical = clues.some(cl =>
                      cl.direction === 'down' && cl.cells.some(([r, c]) => r === rr + 1 && c === cc)
                    );
                    if (!isPartOfVertical && i === 0) canPlace = false;
                  }
                } else {
                  if (cc > 0 && grid[rr][cc - 1] !== '' && (i === 0 || grid[rr][cc] === '')) {
                    const isPartOfHorizontal = clues.some(cl =>
                      cl.direction === 'across' && cl.cells.some(([r, c]) => r === rr && c === cc - 1)
                    );
                    if (!isPartOfHorizontal && i === 0) canPlace = false;
                  }
                  if (cc < gs - 1 && grid[rr][cc + 1] !== '' && (i === 0 || grid[rr][cc] === '')) {
                    const isPartOfHorizontal = clues.some(cl =>
                      cl.direction === 'across' && cl.cells.some(([r, c]) => r === rr && c === cc + 1)
                    );
                    if (!isPartOfHorizontal && i === 0) canPlace = false;
                  }
                }
              }

              if (!canPlace) continue;

              // Place word
              const cells: [number, number][] = [];
              for (let i = 0; i < word.length; i++) {
                const rr = dir === 'across' ? startR : startR + i;
                const cc = dir === 'across' ? startC + i : startC;
                grid[rr][cc] = word[i];
                cells.push([rr, cc]);
              }
              clues.push({
                word: w.word,
                definition: w.definition,
                row: startR,
                col: startC,
                direction: dir,
                number: nextNumber,
                cells,
              });
              nextNumber++;
              placed = true;
            }
          }
        }
      }
    }

    // Strategy 2: place randomly if no intersection found
    if (!placed) {
      for (const dir of directions) {
        if (placed) break;
        const attempts = 80;
        for (let attempt = 0; attempt < attempts && !placed; attempt++) {
          let startR: number, startC: number;
          if (dir === 'across') {
            startR = Math.floor(Math.random() * gs);
            startC = Math.floor(Math.random() * (gs - word.length + 1));
          } else {
            startR = Math.floor(Math.random() * (gs - word.length + 1));
            startC = Math.floor(Math.random() * gs);
          }

          let canPlace = true;
          for (let i = 0; i < word.length && canPlace; i++) {
            const rr = dir === 'across' ? startR : startR + i;
            const cc = dir === 'across' ? startC + i : startC;
            if (grid[rr][cc] !== '' && grid[rr][cc] !== word[i]) canPlace = false;
          }
          if (!canPlace) continue;

          // Check no adjacent conflicts
          for (let i = 0; i < word.length && canPlace; i++) {
            const rr = dir === 'across' ? startR : startR + i;
            const cc = dir === 'across' ? startC + i : startC;
            if (dir === 'across') {
              if (rr > 0 && grid[rr - 1][cc] !== '') {
                const isVertical = clues.some(cl =>
                  cl.direction === 'down' && cl.cells.some(([r, c]) => r === rr - 1 && c === cc)
                );
                if (!isVertical && i === 0) canPlace = false;
              }
              if (rr < gs - 1 && grid[rr + 1][cc] !== '') {
                const isVertical = clues.some(cl =>
                  cl.direction === 'down' && cl.cells.some(([r, c]) => r === rr + 1 && c === cc)
                );
                if (!isVertical && i === 0) canPlace = false;
              }
            } else {
              if (cc > 0 && grid[rr][cc - 1] !== '') {
                const isHorizontal = clues.some(cl =>
                  cl.direction === 'across' && cl.cells.some(([r, c]) => r === rr && c === cc - 1)
                );
                if (!isHorizontal && i === 0) canPlace = false;
              }
              if (cc < gs - 1 && grid[rr][cc + 1] !== '') {
                const isHorizontal = clues.some(cl =>
                  cl.direction === 'across' && cl.cells.some(([r, c]) => r === rr && c === cc + 1)
                );
                if (!isHorizontal && i === 0) canPlace = false;
              }
            }
          }

          if (!canPlace) continue;

          const cells: [number, number][] = [];
          for (let i = 0; i < word.length; i++) {
            const rr = dir === 'across' ? startR : startR + i;
            const cc = dir === 'across' ? startC + i : startC;
            grid[rr][cc] = word[i];
            cells.push([rr, cc]);
          }
          clues.push({
            word: w.word,
            definition: w.definition,
            row: startR,
            col: startC,
            direction: dir,
            number: nextNumber,
            cells,
          });
          nextNumber++;
          placed = true;
        }
      }
    }
  }

  // Fill empty cells with random letters for density
  for (let r = 0; r < gs; r++) {
    for (let c = 0; c < gs; c++) {
      if (grid[r][c] === '') {
        grid[r][c] = '';
      }
    }
  }

  return { grid, clues };
}

export default function MotsCroises() {
  const navigate = useNavigate();
  const { startGame, endGame, gameStatus, gameScore, gameTime, gameErrors, updateScore, updateErrors, unlockAchievement } = useGame();
  const [size, setSize] = useState<GridSize | null>(null);
  const [grid, setGrid] = useState<string[][]>([]);
  const [clues, setClues] = useState<PlacedWord[]>([]);
  const [playerGrid, setPlayerGrid] = useState<string[][]>([]);
  const [selectedCell, setSelectedCell] = useState<[number, number] | null>(null);
  const [direction, setDirection] = useState<'across' | 'down'>('across');
  const [showGameOver, setShowGameOver] = useState(false);
  const [started, setStarted] = useState(false);
  const [revealedCells, setRevealedCells] = useState<Set<string>>(new Set());
  const [foundWords, setFoundWords] = useState<Set<number>>(new Set());
  const gridRef = useRef<HTMLDivElement>(null);

  const gs = grid.length;

  const handleStart = (s: GridSize) => {
    let result: { grid: string[][]; clues: PlacedWord[] };
    let attempts = 0;
    do {
      result = generateCrossword(MEDICAL_WORDS, s);
      attempts++;
    } while (result.clues.length < (s === 'petit' ? 4 : s === 'moyen' ? 7 : 10) && attempts < 20);

    setSize(s);
    setGrid(result.grid);
    setClues(result.clues);
    setPlayerGrid(result.grid.map(row => row.map(() => '')));
    setSelectedCell(null);
    setDirection('across');
    setRevealedCells(new Set());
    setFoundWords(new Set());
    setStarted(true);
    setShowGameOver(false);
    startGame('mots-croises');

    // Auto-select first cell of first word
    if (result.clues.length > 0) {
      const first = result.clues[0];
      setSelectedCell([first.row, first.col]);
      setDirection(first.direction);
    }
  };

  const handleCellClick = (r: number, c: number) => {
    if (grid[r][c] === '') return;
    if (selectedCell && selectedCell[0] === r && selectedCell[1] === c) {
      setDirection(d => d === 'across' ? 'down' : 'across');
    } else {
      setSelectedCell([r, c]);
    }
  };

  // Get the word that contains a cell in a given direction
  const getWordAt = useCallback((r: number, c: number, dir: 'across' | 'down'): PlacedWord | null => {
    return clues.find(cl =>
      cl.direction === dir && cl.cells.some(([cr, cc]) => cr === r && cc === c)
    ) || null;
  }, [clues]);

  // Move to next empty cell in current direction
  const moveNext = useCallback((r: number, c: number, dir: 'across' | 'down', forward = true) => {
    const step = forward ? 1 : -1;
    if (dir === 'across') {
      let nc = c + step;
      while (nc >= 0 && nc < gs) {
        if (grid[r][nc] !== '') {
          setSelectedCell([r, nc]);
          return;
        }
        nc += step;
      }
    } else {
      let nr = r + step;
      while (nr >= 0 && nr < gs) {
        if (grid[nr][c] !== '') {
          setSelectedCell([nr, c]);
          return;
        }
        nr += step;
      }
    }
  }, [gs, grid]);

  const checkWordCompletion = useCallback((newPlayerGrid: string[][]) => {
    const newlyFound = new Set(foundWords);
    let scoreBoost = 0;

    for (const clue of clues) {
      if (newlyFound.has(clue.number)) continue;

      const allFilled = clue.cells.every(([rr, cc]) =>
        newPlayerGrid[rr][cc].toUpperCase() === grid[rr][cc]
      );
      if (allFilled) {
        newlyFound.add(clue.number);
        scoreBoost += 50;

        // Reveal all cells of this word
        const newRevealed = new Set(revealedCells);
        clue.cells.forEach(([rr, cc]) => newRevealed.add(`${rr}-${cc}`));
        setRevealedCells(newRevealed);
      }
    }

    if (scoreBoost > 0) {
      updateScore(gameScore + scoreBoost + 10);
    }

    if (newlyFound.size === clues.length && clues.length > 0) {
      const finalScore = gameScore + scoreBoost + 500;
      updateScore(finalScore);
      unlockAchievement('crossword_pro');
      endGame(finalScore, gameTime, gameErrors);
      setShowGameOver(true);
    } else if (scoreBoost === 0) {
      updateScore(gameScore + 10);
    }

    setFoundWords(newlyFound);
  }, [clues, foundWords, grid, gameScore, gameTime, gameErrors, updateScore, endGame, unlockAchievement, revealedCells]);

  const handleLetterInput = useCallback((letter: string) => {
    if (!selectedCell) return;
    const [r, c] = selectedCell;
    if (grid[r][c] === '') return;

    const up = letter.toUpperCase();
    const newGrid = playerGrid.map(row => [...row]);
    newGrid[r][c] = up;
    setPlayerGrid(newGrid);

    if (up !== grid[r][c]) {
      updateErrors(gameErrors + 1);
    }

    // Check completions
    setTimeout(() => checkWordCompletion(newGrid), 0);

    // Move to next cell in current word
    moveNext(r, c, direction, true);
  }, [selectedCell, direction, grid, playerGrid, gameErrors, updateErrors, checkWordCompletion, moveNext]);

  const handleBackspace = useCallback(() => {
    if (!selectedCell) return;
    const [r, c] = selectedCell;
    const newGrid = playerGrid.map(row => [...row]);
    newGrid[r][c] = '';
    setPlayerGrid(newGrid);
    moveNext(r, c, direction, false);
  }, [selectedCell, direction, playerGrid, moveNext]);

  // Keyboard support
  useEffect(() => {
    if (!started) return;
    const handler = (e: KeyboardEvent) => {
      if (showGameOver) return;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (selectedCell) {
          const [r, c] = selectedCell;
          let nc = c + 1;
          while (nc < gs && grid[r][nc] === '') nc++;
          if (nc < gs) { setSelectedCell([r, nc]); setDirection('across'); }
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (selectedCell) {
          const [r, c] = selectedCell;
          let nc = c - 1;
          while (nc >= 0 && grid[r][nc] === '') nc--;
          if (nc >= 0) { setSelectedCell([r, nc]); setDirection('across'); }
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (selectedCell) {
          const [r, c] = selectedCell;
          let nr = r + 1;
          while (nr < gs && grid[nr][c] === '') nr++;
          if (nr < gs) { setSelectedCell([nr, c]); setDirection('down'); }
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (selectedCell) {
          const [r, c] = selectedCell;
          let nr = r - 1;
          while (nr >= 0 && grid[nr][c] === '') nr--;
          if (nr >= 0) { setSelectedCell([nr, c]); setDirection('down'); }
        }
      } else if (e.key === ' ') {
        e.preventDefault();
        setDirection(d => d === 'across' ? 'down' : 'across');
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        handleBackspace();
      } else if (/^[A-Z]$/i.test(e.key)) {
        e.preventDefault();
        handleLetterInput(e.key);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [started, showGameOver, selectedCell, gs, grid, direction, handleLetterInput, handleBackspace]);

  // Scroll selected cell into view
  useEffect(() => {
    if (!selectedCell || !gridRef.current) return;
    const cell = gridRef.current.querySelector(`[data-cell="${selectedCell[0]}-${selectedCell[1]}"]`) as HTMLElement;
    if (cell) {
      cell.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [selectedCell]);

  const handleReplay = () => { if (size) handleStart(size); };
  const handleQuit = () => { endGame(gameScore, gameTime, gameErrors); navigate('/jeux', { replace: true }); };

  const acrossClues = clues.filter(c => c.direction === 'across');
  const downClues = clues.filter(c => c.direction === 'down');

  // Highlight cells of the current word
  const currentWord = selectedCell ? getWordAt(selectedCell[0], selectedCell[1], direction) : null;

  // Build cell numbers map
  const cellNumbers = useMemo(() => {
    const map: Record<string, number> = {};
    for (const clue of clues) {
      const key = `${clue.row}-${clue.col}`;
      if (!map[key] || clue.number < map[key]) {
        map[key] = clue.number;
      }
    }
    return map;
  }, [clues]);

  // Check if a cell is part of the current active word
  const isInCurrentWord = useCallback((r: number, c: number) => {
    if (!currentWord) return false;
    return currentWord.cells.some(([cr, cc]) => cr === r && cc === c);
  }, [currentWord]);

  if (!started) {
    return (
      <GameWrapper gameName="Mots Croisés Santé" onQuit={handleQuit} showPause={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-3xl p-8 max-w-md w-full text-center border border-background-200/70">
            <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-text-line text-3xl text-accent-600"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Mots Croisés Santé</h2>
            <p className="text-foreground-600 text-sm mb-6">Remplis la grille avec les mots correspondant aux définitions. Utilise ton clavier pour écrire et les flèches pour te déplacer.</p>
            <p className="text-foreground-600 text-sm mb-6">Taille de la grille :</p>
            <div className="grid grid-cols-1 gap-3">
              {(['petit', 'moyen', 'grand'] as GridSize[]).map((s) => (
                <button
                  key={s}
                  onClick={() => handleStart(s)}
                  className="py-3 px-4 bg-secondary-100 hover:bg-secondary-200 rounded-xl text-sm font-medium text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {s === 'petit' ? 'Petite (6 mots)' : s === 'moyen' ? 'Moyenne (10 mots)' : 'Grande (15 mots)'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </GameWrapper>
    );
  }

  if (clues.length === 0) {
    return (
      <GameWrapper gameName="Mots Croisés Santé" onQuit={handleQuit} showPause={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-3xl p-8 max-w-md w-full text-center border border-background-200/70">
            <p className="text-foreground-600 mb-4">Impossible de générer une grille. Réessaie !</p>
            <button
              onClick={() => size && handleStart(size)}
              className="py-3 px-6 bg-primary-500 hover:bg-primary-600 rounded-xl text-sm font-medium text-background-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Réessayer
            </button>
          </div>
        </div>
      </GameWrapper>
    );
  }

  return (
    <GameWrapper gameName="Mots Croisés Santé" onQuit={handleQuit}>
      <div className="flex-1 flex flex-col lg:flex-row gap-6 p-4 max-w-6xl mx-auto w-full h-full overflow-hidden">
        {/* Grid */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="flex-1 overflow-auto flex items-start justify-center">
            <div
              ref={gridRef}
              className="inline-block border-2 border-foreground-800 rounded-lg overflow-hidden select-none"
              style={{ lineHeight: 0 }}
            >
              {grid.map((row, r) => (
                <div key={r} className="flex" style={{ lineHeight: 0 }}>
                  {row.map((cell, c) => {
                    const isSelected = selectedCell && selectedCell[0] === r && selectedCell[1] === c;
                    const isHighlighted = isInCurrentWord(r, c);
                    const isRevealed = revealedCells.has(`${r}-${c}`);
                    const cellNum = cellNumbers[`${r}-${c}`];
                    const playerVal = playerGrid[r][c];
                    const isCorrect = playerVal.toUpperCase() === grid[r][c] && playerVal !== '';
                    const isWrong = playerVal !== '' && playerVal.toUpperCase() !== grid[r][c];

                    if (cell === '') {
                      return (
                        <div
                          key={c}
                          className="w-8 h-8 md:w-10 md:h-10 bg-foreground-900 inline-block"
                          style={{ minWidth: 32, minHeight: 32 }}
                        />
                      );
                    }

                    return (
                      <button
                        key={c}
                        data-cell={`${r}-${c}`}
                        onClick={() => handleCellClick(r, c)}
                        className={`relative w-8 h-8 md:w-10 md:h-10 inline-flex items-center justify-center text-sm md:text-base font-bold border border-foreground-300 cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-primary-500 text-background-50 border-primary-600 z-10'
                            : isHighlighted
                            ? 'bg-primary-100 text-foreground-950'
                            : isRevealed
                            ? 'bg-accent-100 text-accent-900'
                            : 'bg-background-50 text-foreground-950 hover:bg-background-100'
                        } ${isWrong && !isSelected ? 'text-red-600' : ''}`}
                        style={{ minWidth: 32, minHeight: 32 }}
                      >
                        {cellNum && (
                          <span className="absolute top-0.5 left-0.5 text-[7px] md:text-[8px] font-bold text-foreground-700 leading-none">
                            {cellNum}
                          </span>
                        )}
                        {playerVal || ''}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Virtual keyboard */}
          <div className="mt-3 flex-shrink-0">
            <div className="flex flex-wrap gap-1 justify-center max-w-lg mx-auto">
              {'AZERTYUIOP'.split('').map(l => (
                <button
                  key={l}
                  onClick={() => handleLetterInput(l)}
                  className="w-8 h-8 md:w-9 md:h-9 bg-background-100 hover:bg-primary-100 rounded text-xs md:text-sm font-bold border border-background-200 cursor-pointer transition-colors flex items-center justify-center"
                >
                  {l}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1 justify-center max-w-lg mx-auto mt-1">
              {'QSDFGHJKLM'.split('').map(l => (
                <button
                  key={l}
                  onClick={() => handleLetterInput(l)}
                  className="w-8 h-8 md:w-9 md:h-9 bg-background-100 hover:bg-primary-100 rounded text-xs md:text-sm font-bold border border-background-200 cursor-pointer transition-colors flex items-center justify-center"
                >
                  {l}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1 justify-center max-w-lg mx-auto mt-1">
              {'WXCVBN'.split('').map(l => (
                <button
                  key={l}
                  onClick={() => handleLetterInput(l)}
                  className="w-8 h-8 md:w-9 md:h-9 bg-background-100 hover:bg-primary-100 rounded text-xs md:text-sm font-bold border border-background-200 cursor-pointer transition-colors flex items-center justify-center"
                >
                  {l}
                </button>
              ))}
              <button
                onClick={handleBackspace}
                className="w-12 h-8 md:w-14 md:h-9 bg-red-100 hover:bg-red-200 rounded text-xs md:text-sm font-bold border border-red-200 cursor-pointer transition-colors flex items-center justify-center"
              >
                <i className="ri-delete-back-line"></i>
              </button>
              <button
                onClick={() => setDirection(d => d === 'across' ? 'down' : 'across')}
                className="h-8 md:h-9 px-2 bg-secondary-100 hover:bg-secondary-200 rounded text-xs md:text-sm font-bold border border-secondary-200 cursor-pointer transition-colors flex items-center justify-center whitespace-nowrap"
              >
                {direction === 'across' ? '→ Horizontal' : '↓ Vertical'}
              </button>
            </div>
          </div>
        </div>

        {/* Clues panel */}
        <div className="w-full lg:w-72 flex-shrink-0 flex flex-col gap-4 overflow-hidden">
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-3 flex-1 overflow-hidden flex flex-col">
            <h3 className="text-sm font-bold text-foreground-950 mb-2 flex items-center gap-2">
              <i className="ri-arrow-right-line text-primary-500"></i>
              Horizontal ({acrossClues.length})
            </h3>
            <div className="overflow-y-auto flex-1 space-y-1.5 pr-1">
              {acrossClues.map((clue) => {
                const isFound = foundWords.has(clue.number);
                return (
                  <button
                    key={clue.number}
                    onClick={() => {
                      setSelectedCell([clue.row, clue.col]);
                      setDirection('across');
                    }}
                    className={`text-left w-full text-xs leading-relaxed p-1.5 rounded-md transition-colors cursor-pointer ${
                      isFound
                        ? 'bg-accent-100/60 text-accent-900 line-through opacity-70'
                        : currentWord?.number === clue.number
                        ? 'bg-primary-100 text-foreground-950'
                        : 'hover:bg-background-100 text-foreground-700'
                    }`}
                  >
                    <span className={`font-bold ${isFound ? 'text-accent-700' : 'text-primary-600'}`}>
                      {clue.number}.
                    </span>{' '}
                    {clue.definition}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-background-50 rounded-xl border border-background-200/70 p-3 flex-1 overflow-hidden flex flex-col">
            <h3 className="text-sm font-bold text-foreground-950 mb-2 flex items-center gap-2">
              <i className="ri-arrow-down-line text-accent-500"></i>
              Vertical ({downClues.length})
            </h3>
            <div className="overflow-y-auto flex-1 space-y-1.5 pr-1">
              {downClues.map((clue) => {
                const isFound = foundWords.has(clue.number);
                return (
                  <button
                    key={clue.number}
                    onClick={() => {
                      setSelectedCell([clue.row, clue.col]);
                      setDirection('down');
                    }}
                    className={`text-left w-full text-xs leading-relaxed p-1.5 rounded-md transition-colors cursor-pointer ${
                      isFound
                        ? 'bg-accent-100/60 text-accent-900 line-through opacity-70'
                        : currentWord?.number === clue.number
                        ? 'bg-primary-100 text-foreground-950'
                        : 'hover:bg-background-100 text-foreground-700'
                    }`}
                  >
                    <span className={`font-bold ${isFound ? 'text-accent-700' : 'text-accent-600'}`}>
                      {clue.number}.
                    </span>{' '}
                    {clue.definition}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="text-xs text-foreground-500 text-center">
            {foundWords.size} / {clues.length} mots trouvés
          </div>
        </div>
      </div>

      {showGameOver && (
        <GameOverModal
          score={gameScore}
          timeElapsed={gameTime}
          errors={gameErrors}
          gameName="mots-croises"
          message="Grille complétée !"
          onReplay={handleReplay}
          onQuit={handleQuit}
          healthTip={HEALTH_TIPS[Math.floor(Math.random() * HEALTH_TIPS.length)]}
        />
      )}
    </GameWrapper>
  );
}